import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { Consumable, ConsumableParams, CreateConsumableDTO, UpdateConsumableDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useConsumables(initialParams?: ConsumableParams) {
  const { inventoryRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<Consumable> | null>(null);
  const [lowStockItems, setLowStockItems] = useState<Consumable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<ConsumableParams>(initialParams || { page: 1, limit: 20 });

  const fetchConsumables = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryRepo.getConsumables(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar consumibles');
    } finally {
      setLoading(false);
    }
  }, [inventoryRepo, params]);

  const fetchLowStockItems = useCallback(async () => {
    try {
      const result = await inventoryRepo.getLowStockConsumables();
      setLowStockItems(result);
    } catch (err) {
      console.error('Error fetching low stock items:', err);
    }
  }, [inventoryRepo]);

  useEffect(() => {
    fetchConsumables();
  }, [fetchConsumables]);

  useEffect(() => {
    fetchLowStockItems();
  }, [fetchLowStockItems]);

  const search = useCallback((searchTerm: string) => {
    setParams(prev => ({ ...prev, search: searchTerm, page: 1 }));
  }, []);

  const filterByLowStock = useCallback((lowStock: boolean | undefined) => {
    setParams(prev => ({ ...prev, lowStock, page: 1 }));
  }, []);

  const filterByLocation = useCallback((location: string | undefined) => {
    setParams(prev => ({ ...prev, location, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const createConsumable = useCallback(async (data: CreateConsumableDTO) => {
    const newConsumable = await inventoryRepo.createConsumable(data);
    fetchConsumables();
    return newConsumable;
  }, [inventoryRepo, fetchConsumables]);

  const updateConsumable = useCallback(async (id: string, data: UpdateConsumableDTO) => {
    const updated = await inventoryRepo.updateConsumable(id, data);
    fetchConsumables();
    fetchLowStockItems();
    return updated;
  }, [inventoryRepo, fetchConsumables, fetchLowStockItems]);

  const deleteConsumable = useCallback(async (id: string) => {
    await inventoryRepo.deleteConsumable(id);
    fetchConsumables();
    fetchLowStockItems();
  }, [inventoryRepo, fetchConsumables, fetchLowStockItems]);

  const getConsumableById = useCallback(async (id: string) => {
    return await inventoryRepo.getConsumableById(id);
  }, [inventoryRepo]);

  return {
    consumables: data?.data || [],
    pagination: data?.meta,
    lowStockItems,
    loading,
    error,
    params,
    search,
    filterByLowStock,
    filterByLocation,
    goToPage,
    createConsumable,
    updateConsumable,
    deleteConsumable,
    getConsumableById,
    refresh: fetchConsumables,
    refreshLowStock: fetchLowStockItems,
  };
}
